import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { formatHoursMinutes } from '../utils/dateUtils';
import {
  Box, Typography, TextField, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Chip, IconButton,
  CircularProgress, Tabs, Tab, Button, Radio, ListItemIcon,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Download, AccessTime, AttachMoney,
  PeopleAlt, TrendingUp,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, AreaChart,
} from 'recharts';
import apiClient from '../api/client';
import { Client } from '../types';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Types ──

interface MonthlyHoursRow {
  employee_id: string;
  employee_name: string;
  employee_uid: string;
  expected_daily_hours: number;
  hourly_rate: number | null;
  client_name: string;
  client_code: string;
  timesheet_id: string | null;
  status: string | null;
  total_hours: number;
  submitted_at: string | null;
  total_break_minutes: number;
  weekday_count: number;
}

interface ClientUtilizationRow {
  client_id: string;
  client_name: string;
  client_code: string;
  employee_count: number;
  total_billable_hours: number;
  total_break_minutes: number;
  employees: Array<{
    employee_id: string;
    employee_name: string;
    hourly_rate: number | null;
    total_hours: number;
  }>;
}

interface SubmissionTrackerData {
  months: Array<{ month: number; year: number }>;
  employees: Array<{
    employee_id: string;
    employee_name: string;
    employee_uid: string;
    client_name: string;
    timesheets: Record<string, string>;
  }>;
}

interface AnalyticsData {
  stats: {
    active_employees: number;
    active_clients: number;
    total_billable_hours: number;
    total_revenue: number;
    avg_hours_per_employee: number;
    avg_client_rate: number;
    revenue_ytd: number;
  };
  clientRevenue: Array<{
    client_name: string;
    client_code: string;
    employee_count: number;
    billable_hours: number;
    revenue: number;
  }>;
  revenueTrend: Array<{
    label: string;
    month: number;
    year: number;
    billable_hours: number;
    revenue: number;
  }>;
  topEmployees: Array<{
    employee_name: string;
    employee_uid: string;
    client_name: string;
    hourly_rate: number;
    billable_hours: number;
    revenue: number;
  }>;
  currentMonth: number;
  currentYear: number;
}

// ── Helpers ──

const statusDisplayLabels: Record<string, string> = {
  draft: 'In Progress',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  missing: 'Missing',
};

function getVarianceColor(variance: number, expectedHours: number): string {
  if (expectedHours === 0) return '#64748B';
  const pct = (variance / expectedHours) * 100;
  if (pct >= -5) return '#10B981';
  if (pct >= -15) return '#F59E0B';
  return '#EF4444';
}

function getStatusChip(status: string) {
  const label = statusDisplayLabels[status] || status;
  switch (status) {
    case 'submitted':
      return <Chip label={label} size="small" sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 500, fontSize: 11, height: 24 }} />;
    case 'approved':
      return <Chip label={label} size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 500, fontSize: 11, height: 24 }} />;
    case 'draft':
      return <Chip label={label} size="small" sx={{ backgroundColor: '#F1F5F9', color: '#334155', fontWeight: 500, fontSize: 11, height: 24 }} />;
    case 'rejected':
      return <Chip label={label} size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', fontWeight: 500, fontSize: 11, height: 24 }} />;
    case 'missing':
    default:
      return <Chip label="Missing" size="small" sx={{ backgroundColor: '#F1F5F9', color: '#94A3B8', fontWeight: 500, fontSize: 11, height: 24 }} />;
  }
}

function formatCurrency(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

// ── Custom Tooltip Components ──

function BarChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 1.5,
      px: 2, py: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: '#10B981' }}>
        Revenue: ${Number(payload[0]?.value || 0).toLocaleString()}
      </Typography>
      {payload[1] && (
        <Typography sx={{ fontSize: 12, color: '#3B82F6' }}>
          Hours: {Number(payload[1]?.value || 0).toFixed(1)}
        </Typography>
      )}
    </Box>
  );
}

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 1.5,
      px: 2, py: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: '#8B5CF6' }}>
        Revenue: ${Number(payload[0]?.value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

// ── Main Component ──

export default function AdminReportsPage() {
  const now = new Date();
  const [tab, setTab] = useState(0);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [clients, setClients] = useState<Client[]>([]);
  const [clientFilter, setClientFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Data
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyHoursRow[]>([]);
  const [clientData, setClientData] = useState<ClientUtilizationRow[]>([]);
  const [trackerData, setTrackerData] = useState<SubmissionTrackerData | null>(null);

  // Month navigation
  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Load clients once
  useEffect(() => {
    apiClient.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 0) {
        const { data } = await apiClient.get('/reports/analytics', { params: { month, year } });
        setAnalyticsData(data);
      } else if (tab === 1) {
        const params: Record<string, any> = { month, year };
        if (clientFilter !== 'all') params.client_id = clientFilter;
        const { data } = await apiClient.get('/reports/monthly-hours', { params });
        setMonthlyData(data);
      } else if (tab === 2) {
        const { data } = await apiClient.get('/reports/client-utilization', { params: { month, year } });
        setClientData(data);
      } else if (tab === 3) {
        const { data } = await apiClient.get('/reports/submission-tracker');
        setTrackerData(data);
      }
    } catch (err) {
      console.error('Report fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [tab, month, year, clientFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // CSV export for Monthly Hours
  const handleExportCSV = () => {
    const headers = ['Employee', 'UID', 'Client', 'Total Hours', 'Expected Hours', 'Variance', 'Break (hrs)', 'Status'];
    const rows = monthlyData.map(row => {
      const expectedHours = Number(row.weekday_count) * Number(row.expected_daily_hours);
      const variance = Number(row.total_hours || 0) - expectedHours;
      const breakHrs = (Number(row.total_break_minutes || 0) / 60).toFixed(1);
      const statusLabel = row.status ? (statusDisplayLabels[row.status] || row.status) : 'Missing';
      return [
        row.employee_name, row.employee_uid, row.client_name,
        formatHoursMinutes(Number(row.total_hours || 0)), expectedHours.toFixed(1),
        variance.toFixed(1), breakHrs, statusLabel,
      ].map(v => `"${v}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly-hours-${MONTH_ABBR[month - 1].toLowerCase()}-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Check if any client utilization row has cost data
  const showCostColumn = useMemo(() => {
    return clientData.some(row =>
      row.employees.some(emp => emp.hourly_rate && emp.hourly_rate > 0)
    );
  }, [clientData]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>Reports & Analytics</Typography>
          <Typography sx={{ fontSize: 13, color: '#94A3B8', mt: 0.25 }}>Billing, revenue, and workforce insights</Typography>
        </Box>
        {tab === 1 && monthlyData.length > 0 && (
          <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleExportCSV}
            sx={{ textTransform: 'none' }}>
            Export CSV
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2.5,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: 13.5, minHeight: 40 },
          '& .Mui-selected': { fontWeight: 600 },
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        <Tab label="Dashboard" />
        <Tab label="Monthly Hours" />
        <Tab label="Client Utilization" />
        <Tab label="Submission Tracker" />
      </Tabs>

      {/* Month navigation — for tabs 0, 1, 2 */}
      {tab !== 3 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={prevMonth}><ChevronLeft /></IconButton>
            <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 140, textAlign: 'center' }}>
              {MONTHS[month - 1]} {year}
            </Typography>
            <IconButton size="small" onClick={nextMonth}><ChevronRight /></IconButton>
          </Box>

          {/* Client filter — only on Monthly Hours tab */}
          {tab === 1 && (
            <TextField
              select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
              size="small" sx={{ minWidth: 180 }} label="Client"
            >
              <MenuItem value="all">
                <ListItemIcon sx={{ minWidth: 32 }}><Radio checked={clientFilter === 'all'} size="small" /></ListItemIcon>
                All Clients
              </MenuItem>
              {clients.filter(c => c.status === 'active' && !c.is_test).map(c => (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemIcon sx={{ minWidth: 32 }}><Radio checked={clientFilter === c.id} size="small" /></ListItemIcon>
                  {c.client_name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          {tab === 0 && analyticsData && <AnalyticsDashboard data={analyticsData} month={month} year={year} />}
          {tab === 1 && <MonthlyHoursTable data={monthlyData} month={month} year={year} />}
          {tab === 2 && <ClientUtilizationTable data={clientData} month={month} year={year} showCost={showCostColumn} />}
          {tab === 3 && trackerData && <SubmissionTrackerGrid data={trackerData} />}
        </>
      )}
    </Box>
  );
}

// ── Tab 0: Analytics Dashboard ──

function AnalyticsDashboard({ data, month, year }: { data: AnalyticsData; month: number; year: number }) {
  const { stats, clientRevenue, revenueTrend, topEmployees } = data;

  const statCards = [
    {
      label: 'Billable Hours',
      value: stats.total_billable_hours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      icon: <AccessTime />,
      color: '#10B981',
      bg: '#ECFDF5',
    },
    {
      label: `Revenue (${MONTHS[month - 1]})`,
      value: `$${stats.total_revenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <AttachMoney />,
      color: '#8B5CF6',
      bg: '#EDE9FE',
    },
    {
      label: 'Active Employees',
      value: stats.active_employees.toString(),
      icon: <PeopleAlt />,
      color: '#3B82F6',
      bg: '#EFF6FF',
    },
    {
      label: 'Avg Client Rate',
      value: `$${stats.avg_client_rate.toFixed(0)}/hr`,
      icon: <TrendingUp />,
      color: '#F59E0B',
      bg: '#FEF3C7',
    },
    {
      label: `Revenue YTD ${year}`,
      value: `$${(stats.revenue_ytd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <TrendingUp />,
      color: '#EF4444',
      bg: '#FEF2F2',
    },
  ];

  return (
    <Box>
      {/* Stat Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        {statCards.map((card) => (
          <Paper
            key={card.label}
            variant="outlined"
            sx={{
              px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 2,
              borderLeft: `3px solid ${card.color}`,
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', transform: 'translateY(-1px)' },
            }}
          >
            <Box sx={{
              width: 48, height: 48, borderRadius: 2, backgroundColor: card.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: card.color, flexShrink: 0,
            }}>
              {card.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 600, color: '#94A3B8',
                textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.2,
              }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#0F172A', lineHeight: 1.3, letterSpacing: '-0.02em' }}>
                {card.value}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Charts Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5, mb: 3 }}>
        {/* Revenue by Client — Bar Chart */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Revenue by Client
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              {MONTHS[month - 1]}
            </Typography>
          </Box>
          {clientRevenue.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={clientRevenue} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="client_name" tick={{ fontSize: 11, fill: '#64748B' }}
                  axisLine={{ stroke: '#E2E8F0' }} tickLine={false}
                  interval={0} angle={clientRevenue.length > 4 ? -25 : 0}
                  textAnchor={clientRevenue.length > 4 ? 'end' : 'middle'}
                  height={clientRevenue.length > 4 ? 60 : 30}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => formatCurrency(v)} width={55}
                />
                <Tooltip content={<BarChartTooltip />} cursor={{ fill: '#F8FAFC' }} />
                <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={52} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary" fontSize={13}>No revenue data for this month</Typography>
            </Box>
          )}
        </Paper>

        {/* Revenue Trend — Area Chart */}
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Revenue Per Month
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              {month === 1
                ? `Jan '${String(year).slice(2)}`
                : `Jan – ${MONTH_ABBR[month - 1]} '${String(year).slice(2)}`}
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis
                dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }}
                axisLine={{ stroke: '#E2E8F0' }} tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => formatCurrency(v)} width={55}
              />
              <Tooltip content={<TrendTooltip />} />
              <Area
                type="monotone" dataKey="revenue" stroke="#8B5CF6" strokeWidth={2.5}
                fill="url(#revenueGradient)" dot={{ r: 4, fill: '#8B5CF6', strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 6, fill: '#8B5CF6', strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Bottom Row: Top Employees + Client Breakdown Table */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
        {/* Top Employees by Revenue */}
        <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Top Employees by Revenue
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                      No data for this month
                    </TableCell>
                  </TableRow>
                ) : topEmployees.map((emp, idx) => (
                  <TableRow key={emp.employee_uid} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          backgroundColor: idx < 3 ? '#EFF6FF' : '#F8FAFC',
                          color: idx < 3 ? '#3B82F6' : '#94A3B8',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}>
                          {idx + 1}
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                            {emp.employee_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 11 }}>
                            {emp.client_name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontSize={13}>{emp.billable_hours.toFixed(1)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontSize={13} color="text.secondary">
                        {emp.hourly_rate > 0 ? `$${emp.hourly_rate}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontSize={13} fontWeight={600} sx={{ color: '#0F172A' }}>
                        {emp.revenue > 0 ? `$${emp.revenue.toLocaleString()}` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Client Breakdown */}
        <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #E2E8F0' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0F172A' }}>
              Client Breakdown
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Team</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientRevenue.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                      No data for this month
                    </TableCell>
                  </TableRow>
                ) : clientRevenue.map((row) => {
                  const totalRevenue = data.stats.total_revenue || 1;
                  const pct = Math.round((row.revenue / totalRevenue) * 100);
                  return (
                    <TableRow key={row.client_code} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', fontSize: 13 }}>
                            {row.client_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box sx={{
                              width: `${Math.max(pct, 4)}%`, maxWidth: '80%', height: 4,
                              borderRadius: 2, backgroundColor: '#10B981', opacity: 0.6,
                            }} />
                            <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>
                              {pct}%
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.employee_count} size="small" variant="outlined"
                          sx={{ fontSize: 12, fontWeight: 600, minWidth: 32 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13}>{row.billable_hours.toFixed(1)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontSize={13} fontWeight={600} sx={{ color: '#0F172A' }}>
                          {row.revenue > 0 ? `$${row.revenue.toLocaleString()}` : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}

// ── Tab 1: Monthly Hours Summary ──

function MonthlyHoursTable({ data, month, year }: { data: MonthlyHoursRow[]; month: number; year: number }) {
  if (data.length === 0) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No data for {MONTHS[month - 1]} {year}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  let totalHoursSum = 0;
  let expectedHoursSum = 0;
  let breakMinutesSum = 0;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 140 }}>Client</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Total Hrs</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 110 }} align="right">Expected Hrs</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Variance</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 90 }} align="right">Break (hrs)</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 110 }} align="center">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => {
            const expectedHours = Number(row.weekday_count) * Number(row.expected_daily_hours);
            const totalHours = Number(row.total_hours || 0);
            const variance = totalHours - expectedHours;
            const breakHrs = Number(row.total_break_minutes || 0) / 60;
            const hasTsData = row.timesheet_id != null;

            totalHoursSum += totalHours;
            expectedHoursSum += expectedHours;
            breakMinutesSum += Number(row.total_break_minutes || 0);

            return (
              <TableRow key={row.employee_id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{row.employee_name}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11, color: '#94A3B8' }}>{row.employee_uid}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize={13}>{row.client_name}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>{hasTsData ? formatHoursMinutes(totalHours) : '—'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{expectedHours.toFixed(1)}</Typography>
                </TableCell>
                <TableCell align="right">
                  {hasTsData ? (
                    <Typography variant="body2" fontWeight={600} sx={{ color: getVarianceColor(variance, expectedHours) }}>
                      {variance >= 0 ? '+' : ''}{variance.toFixed(1)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{hasTsData ? breakHrs.toFixed(1) : '—'}</Typography>
                </TableCell>
                <TableCell align="center">
                  {getStatusChip(row.status || 'missing')}
                </TableCell>
              </TableRow>
            );
          })}

          {/* Totals row */}
          <TableRow sx={{ backgroundColor: '#F0F4FF' }}>
            <TableCell colSpan={2}>
              <Typography variant="body2" fontWeight={700}>Total ({data.length} employees)</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" fontWeight={700}>{formatHoursMinutes(totalHoursSum)}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" fontWeight={600} color="text.secondary">{expectedHoursSum.toFixed(1)}</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" fontWeight={700} sx={{ color: getVarianceColor(totalHoursSum - expectedHoursSum, expectedHoursSum) }}>
                {(totalHoursSum - expectedHoursSum) >= 0 ? '+' : ''}{(totalHoursSum - expectedHoursSum).toFixed(1)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" fontWeight={600} color="text.secondary">{(breakMinutesSum / 60).toFixed(1)}</Typography>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Tab 2: Client Utilization ──

function ClientUtilizationTable({ data, month, year, showCost }: { data: ClientUtilizationRow[]; month: number; year: number; showCost: boolean }) {
  if (data.length === 0) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell colSpan={showCost ? 6 : 5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No data for {MONTHS[month - 1]} {year}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 100 }} align="center">Employees</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 150 }} align="right">Total Billable Hrs</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 100 }} align="right">Break Hrs</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 150 }} align="right">Avg Hrs/Employee</TableCell>
            {showCost && <TableCell sx={{ fontWeight: 600, width: 130 }} align="right">Est. Cost</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => {
            const totalBillable = Number(row.total_billable_hours || 0);
            const breakHrs = Number(row.total_break_minutes || 0) / 60;
            const avgHrs = row.employee_count > 0 ? totalBillable / row.employee_count : 0;
            const estimatedCost = row.employees.reduce((sum, emp) => {
              if (emp.hourly_rate && emp.hourly_rate > 0) {
                return sum + (Number(emp.hourly_rate) * Number(emp.total_hours));
              }
              return sum;
            }, 0);

            return (
              <TableRow key={row.client_id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{row.client_name}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11, color: '#94A3B8' }}>{row.client_code}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.employee_count} size="small" variant="outlined" sx={{ fontSize: 12, fontWeight: 600 }} />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>{totalBillable.toFixed(1)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{breakHrs.toFixed(1)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">{avgHrs.toFixed(1)}</Typography>
                </TableCell>
                {showCost && (
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {estimatedCost > 0 ? `$${estimatedCost.toFixed(2)}` : '—'}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Tab 3: Submission Tracker ──

function SubmissionTrackerGrid({ data }: { data: SubmissionTrackerData }) {
  if (data.employees.length === 0) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No active employees found
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 140 }}>Client</TableCell>
            {data.months.map((m) => (
              <TableCell key={`${m.year}-${m.month}`} sx={{ fontWeight: 600, width: 110 }} align="center">
                {MONTH_ABBR[m.month - 1]} '{String(m.year).slice(2)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.employees.map((emp) => (
            <TableRow key={emp.employee_id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{emp.employee_name}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11, color: '#94A3B8' }}>{emp.employee_uid}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontSize={13}>{emp.client_name}</Typography>
              </TableCell>
              {data.months.map((m) => {
                const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
                const status = emp.timesheets[key] || 'missing';
                return (
                  <TableCell key={key} align="center">
                    {getStatusChip(status)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
